import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Check, Clock, Play, Plus, Printer, ShieldAlert, Timer } from 'lucide-react';
import {
  LEVEL_TONE, createNavigation, createResponse, createStandard, downloadBoardPdf, getConditions,
  getNavigations, getResponses, getStandards, patchResponse, saveAlignment, saveCondition,
  verifyStandard,
} from './api';

const todayIso = () => new Date().toISOString().slice(0, 10);

// ─── K — Know ─────────────────────────────────────────────────────────
export const KnowTab = ({ content, departments, deptSource, date, selectedDept, onSelectDept, onChanged }) => {
  const [rows, setRows] = useState([]);
  const deptId = selectedDept || '';
  const setDeptId = onSelectDept || (() => {});
  const [dims, setDims] = useState({});
  const [counts, setCounts] = useState({});
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const d = await getConditions(date, date);
    setRows(d.rows);
  }, [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const existing = rows.find((r) => r.department_id === deptId);
    setDims(existing?.dimensions || {});
    setCounts(existing?.work_counts || {});
    setRate(existing?.nominal_rate ? String(existing.nominal_rate) : '');
  }, [rows, deptId]);

  const setDim = (key, patch) => setDims((p) => ({ ...p, [key]: { level: 'green', note: '', source: '', ...(p[key] || {}), ...patch } }));

  const movable = content.work_state_definitions.filter((s) => s.movable).reduce((a, s) => a + Number(counts[s.key] || 0), 0);
  const held = Number(counts.blocked || 0) + Number(counts.queued || 0);
  const reported = movable + held;
  const r = Number(rate || 0);
  const hrs = (u) => (r > 0 ? `${(u / r).toFixed(2)} h` : '—');

  const save = async () => {
    setSaving(true);
    try {
      await saveCondition({
        date, department_id: deptId, dimensions: dims,
        work_counts: Object.fromEntries(Object.entries(counts).map(([k, v]) => [k, Number(v || 0)])),
        nominal_rate: r,
      });
      toast.success('Condition set');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const printBoard = async () => {
    const name = departments.find((d) => d.id === deptId)?.name || 'board';
    try { await downloadBoardPdf(deptId, date, name); toast.success('Board exported'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-know">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Condition board · {date}</CardTitle>
            <p className="text-xs text-muted-foreground">
              See the condition before judging performance. Anything that is not green needs a fact, not a feeling.
              The overall level is the worst dimension — one red is not averaged away by seven greens.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={printBoard} disabled={!deptId} data-testid="method-board-export">
            <Printer className="mr-1 h-4 w-4" />Print board
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {departments.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="method-know-no-depts">
                No departments yet — add them in {deptSource?.source || 'FlowCrew™ → Departments'} and they appear here automatically.
              </p>
            )}
            {departments.map((d) => (
              <button key={d.id} onClick={() => setDeptId(d.id)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors cursor-pointer ${deptId === d.id ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-muted text-muted-foreground hover:text-foreground'}`}
                      data-testid={`method-know-dept-${d.id}`}>
                {d.name}
                {rows.find((r) => r.department_id === d.id) && (
                  <span className={`ml-2 inline-block h-2 w-2 rounded-full ${{ green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500', black: 'bg-foreground' }[rows.find((r) => r.department_id === d.id).overall_level]}`} />
                )}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="method-know-dept-source">
            Departments come from {deptSource?.source || 'FlowCrew™ → Departments'} — always your own areas, per tenant.
            “Print board” gives this department a one-page wall board for the date shown, watermarked 改善管理 and locked.
          </p>


          <div className="grid gap-2 sm:grid-cols-2">
            {content.condition_dimensions.map((dim) => {
              const cur = dims[dim.key] || { level: 'green', note: '', source: '' };
              return (
                <div key={dim.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-dim-${dim.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground">{dim.name}</span>
                    <div className="flex gap-1">
                      {content.response_ladder.map((l) => (
                        <button key={l.level} onClick={() => setDim(dim.key, { level: l.level })}
                                title={`${l.condition} — ${l.response}`}
                                className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors cursor-pointer ${cur.level === l.level ? LEVEL_TONE[l.level] : 'border-muted text-muted-foreground'}`}
                                data-testid={`method-dim-${dim.key}-${l.level}`}>
                          {l.level}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">{content.condition_dimension_questions[dim.key]}</p>
                  {cur.level !== 'green' && (
                    <>
                      <Input value={cur.note || ''} onChange={(e) => setDim(dim.key, { note: e.target.value })}
                             placeholder="State the fact — boundary, deadline, what is affected"
                             className="mt-2 h-8 text-xs" data-testid={`method-dim-${dim.key}-note`} />
                      <select value={cur.source || ''} onChange={(e) => setDim(dim.key, { source: e.target.value })}
                              className="mt-2 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                              data-testid={`method-dim-${dim.key}-source`}>
                        <option value="">Evidence source…</option>
                        {content.evidence_sources.map((s) => <option key={s.key} value={s.key}>{s.label} — {s.hint}</option>)}
                      </select>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="method-evidence-rule">
            <strong className="uppercase tracking-wide text-primary">Evidence rule</strong> — {content.evidence_rule}
          </p>
          <Button size="sm" onClick={save} disabled={saving || !deptId} data-testid="method-know-save">
            {saving ? 'Saving…' : 'Set condition'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Work states and the forecast</CardTitle>
          <p className="text-xs text-muted-foreground">{content.forecast_rule}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {content.work_state_definitions.map((s) => (
              <label key={s.key} className="block" data-testid={`method-state-${s.key}`}>
                <span className="text-[11px] font-bold uppercase tracking-wide text-primary">{s.letter} · {s.name}</span>
                <span className="block text-[10px] text-muted-foreground">{s.text}</span>
                <Input type="number" min="0" value={counts[s.key] ?? ''}
                       onChange={(e) => setCounts((p) => ({ ...p, [s.key]: e.target.value }))}
                       className="mt-1 h-8 text-xs" data-testid={`method-state-input-${s.key}`} />
              </label>
            ))}
          </div>
          <label className="block max-w-xs">
            <span className="text-xs font-medium text-foreground">Nominal rate (units per hour)</span>
            <Input type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="method-know-rate" />
          </label>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Reported backlog', reported, 'method-forecast-reported'],
              ['Can actually move', movable, 'method-forecast-movable'],
              ['Blocked or queued', held, 'method-forecast-held'],
              ['Overstated by', reported ? `${((held / reported) * 100).toFixed(1)}%` : '0%', 'method-forecast-overstatement'],
            ].map(([label, value, tid]) => (
              <div key={label} className="rounded-lg border border-muted/60 p-3" data-testid={tid}>
                <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
                <p className="text-[11px] text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="method-forecast-hours">
            Executable workload now <strong className="text-foreground">{hrs(movable)}</strong> · blocked potential
            {' '}<strong className="text-foreground">{hrs(held)}</strong> · all of it together would read
            {' '}<strong className="text-foreground">{hrs(reported)}</strong> — which is the number that misleads the shift.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── A — Align ────────────────────────────────────────────────────────
export const AlignTab = ({ content, date, deptId = '', onChanged }) => {
  const [answers, setAnswers] = useState({});
  const [notDoing, setNotDoing] = useState('');
  const [huddle, setHuddle] = useState({});
  const [checkBack, setCheckBack] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await saveAlignment({
        date, department_id: deptId, answers, not_doing: notDoing.split('\n').filter(Boolean), huddle, check_back: checkBack,
      });
      toast.success('Direction recorded');
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="method-align">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Alignment · {date}</CardTitle>
          <p className="text-xs text-muted-foreground">
            Turn shared reality into shared direction. A direction is executable when the team can repeat it.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {content.alignment_questions.map((q) => (
            <label key={q.key} className="block">
              <span className="text-xs font-medium text-foreground">{q.question}</span>
              <Input value={answers[q.key] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [q.key]: e.target.value }))}
                     className="mt-1 h-9 text-sm" data-testid={`method-align-${q.key}`} />
            </label>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-foreground">What we will NOT do today (one per line)</span>
            <Textarea rows={3} value={notDoing} onChange={(e) => setNotDoing(e.target.value)}
                      className="mt-1 text-sm" data-testid="method-align-not-doing" />
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ten-minute alignment huddle</CardTitle>
          <p className="text-xs text-muted-foreground">Each minute window produces one output. The last one is proof, not opinion.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.huddle.map((h) => (
            <div key={h.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-huddle-${h.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">{h.focus}</span>
                <span className="text-[11px] tabular-nums text-muted-foreground">{h.time}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">{h.question}</p>
              <Input value={huddle[h.key] || ''} onChange={(e) => setHuddle((p) => ({ ...p, [h.key]: e.target.value }))}
                     placeholder={`Output — ${h.output.toLowerCase()}`} className="mt-2 h-8 text-xs"
                     data-testid={`method-huddle-input-${h.key}`} />
            </div>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-foreground">Check-back — what did the team repeat back?</span>
            <Input value={checkBack} onChange={(e) => setCheckBack(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="method-align-checkback" />
          </label>
          <Button size="sm" onClick={save} disabled={saving} data-testid="method-align-save">
            {saving ? 'Saving…' : 'Record direction'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Who aligns what</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {content.alignment_roles.map((r) => (
            <div key={r.label} className="rounded-lg border border-muted/60 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{r.label}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{r.text}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── N — Navigate (the 15-minute route) ───────────────────────────────
export const NavigateTab = ({ content, date, deptId = '', onChanged }) => {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [steps, setSteps] = useState({});
  const [flows, setFlows] = useState({});
  const [digital, setDigital] = useState({});
  const [digitalFirst, setDigitalFirst] = useState('');
  const [evidencePackage, setEvidencePackage] = useState('');
  const [firstLost, setFirstLost] = useState('');
  const [constraint, setConstraint] = useState('');
  const [action, setAction] = useState('');
  const [owner, setOwner] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => setRows((await getNavigations(date, date)).rows), [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const mm = Math.floor(elapsed / 60);
  const activeStep = content.navigation_route.find((s) => mm >= s.from_min && mm < s.to_min) || content.navigation_route[3];

  const commit = async () => {
    try {
      await createNavigation({
        date, department_id: deptId, duration_s: elapsed, steps, flows, digital, first_lost_condition: firstLost,
        digital_first_inconsistent: digitalFirst, evidence_package: evidencePackage,
        constraint, action, owner, return_time: returnTime,
      });
      toast.success('Route committed');
      setRunning(false); setElapsed(0); setSteps({}); setFlows({}); setDigital({});
      setDigitalFirst(''); setEvidencePackage(''); setFirstLost(''); setConstraint(''); setAction('');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-navigate">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">The 15-minute route</CardTitle>
            <p className="text-xs text-muted-foreground">Follow the work until the real constraint appears. One route ends in ONE constraint and ONE action.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg tabular-nums text-foreground" data-testid="method-nav-timer">
              {String(mm).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
            </span>
            <Button size="sm" variant={running ? 'outline' : 'default'} onClick={() => setRunning((r) => !r)} data-testid="method-nav-start">
              {running ? <Timer className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              {running ? 'Pause' : 'Start route'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {content.navigation_route.map((s) => (
            <div key={s.step}
                 className={`rounded-lg border p-3 transition-colors ${running && activeStep.step === s.step ? 'border-primary bg-primary/5' : 'border-muted/60'}`}
                 data-testid={`method-nav-step-${s.step}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground">{s.mode}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">{s.time}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{s.what}</p>
              <Input value={steps[s.step] || ''} onChange={(e) => setSteps((p) => ({ ...p, [s.step]: e.target.value }))}
                     placeholder="What you found" className="mt-2 h-8 text-xs" data-testid={`method-nav-finding-${s.step}`} />
            </div>
          ))}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input value={constraint} onChange={(e) => setConstraint(e.target.value)} placeholder="The constraint (one)" className="h-9 text-sm" data-testid="method-nav-constraint" />
            <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="The committed action (one)" className="h-9 text-sm" data-testid="method-nav-action" />
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Owner" className="h-9 text-sm" data-testid="method-nav-owner" />
            <Input value={returnTime} onChange={(e) => setReturnTime(e.target.value)} placeholder="Return time (e.g. 14:30)" className="h-9 text-sm" data-testid="method-nav-return" />
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="method-constraint-test">
            <strong className="uppercase tracking-wide text-primary">Constraint test</strong> — {content.constraint_test}
          </p>
          <Button size="sm" onClick={commit} disabled={!constraint.trim() || !action.trim()} data-testid="method-nav-commit">
            Commit
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">The four connected flows</CardTitle>
          <p className="text-xs text-muted-foreground">Leave the summary and trace the work. Note where normal condition is first lost.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.navigate_flows.map((f) => (
            <div key={f.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-flow-${f.key}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{f.label}</p>
              <p className="text-[11px] text-muted-foreground">{f.text}</p>
              <Input value={flows[f.key] || ''} onChange={(e) => setFlows((p) => ({ ...p, [f.key]: e.target.value }))}
                     placeholder="What you traced" className="mt-2 h-8 text-xs" data-testid={`method-flow-input-${f.key}`} />
            </div>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-foreground">Where was normal condition first lost?</span>
            <Input value={firstLost} onChange={(e) => setFirstLost(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="method-nav-first-lost" />
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Digital operations — five conditions to compare</CardTitle>
          <p className="text-xs text-muted-foreground">
            When the constraint is created by logic, latency or stale status, compare the five conditions before blaming the floor.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.digital_conditions.map((d) => (
            <div key={d.key} className="rounded-lg border border-muted/60 p-3" data-testid={`method-digital-${d.key}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{d.step} · {d.name}</p>
              <p className="text-[11px] text-muted-foreground">{d.question}</p>
              <Input value={digital[d.key] || ''} onChange={(e) => setDigital((p) => ({ ...p, [d.key]: e.target.value }))}
                     placeholder="What it says" className="mt-2 h-8 text-xs" data-testid={`method-digital-input-${d.key}`} />
            </div>
          ))}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input value={digitalFirst} onChange={(e) => setDigitalFirst(e.target.value)}
                   placeholder="First inconsistent state" className="h-9 text-sm" data-testid="method-digital-first" />
            <Input value={evidencePackage} onChange={(e) => setEvidencePackage(e.target.value)}
                   placeholder="Evidence package — times, IDs, screens, last good transaction" className="h-9 text-sm"
                   data-testid="method-digital-evidence" />
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="method-boundary-rule">
            <strong className="uppercase tracking-wide text-primary">Boundary rule</strong> — {content.boundary_rule}
          </p>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Routes walked today</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="rounded-lg border border-muted/60 p-3 text-xs" data-testid={`method-nav-row-${r.id}`}>
                <p className="font-medium text-foreground">{r.constraint}</p>
                <p className="text-muted-foreground">→ {r.action}{r.owner ? ` · ${r.owner}` : ''}{r.return_time ? ` · back at ${r.return_time}` : ''}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── R — Respond ──────────────────────────────────────────────────────
export const RespondTab = ({ content, date, deptId = '', onChanged }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(null);
  const [closeNote, setCloseNote] = useState('');
  const blank = {
    level: 'amber', condition_summary: '', boundary: '', owner: '', return_time: '', containment: '',
    containment_expires_at: '', removal_condition: '', evidence_preserved: false, risk: '', control: '',
    decision_needed: '', decision_by: '', accountability_flag: false, accountability_note: '',
  };
  const [form, setForm] = useState(blank);
  const [logic, setLogic] = useState({});

  const load = useCallback(async () => setRows((await getResponses('all')).rows), []);
  useEffect(() => { load(); }, [load]);

  const preview = [
    ['Condition', form.condition_summary], ['Boundary', form.boundary], ['Risk', form.risk],
    ['Control', form.control], ['Decision', form.decision_needed], ['Time', form.decision_by],
  ].map(([k, v]) => `${k}: ${v || '—'}`).join(' • ');

  const submit = async () => {
    try {
      await createResponse({ ...form, logic, date, department_id: deptId });
      toast.success('Response opened');
      setOpen(false); setForm(blank); setLogic({});
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const close = async () => {
    try {
      await patchResponse(closing, { status: 'closed', closing_note: closeNote });
      setClosing(null); setCloseNote('');
      await load(); onChanged?.();
      toast.success('Closed on a verified result');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-respond">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Response ladder</CardTitle>
            <p className="text-xs text-muted-foreground">Restore control without destroying the evidence.</p>
          </div>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="method-respond-new"><Plus className="h-4 w-4 mr-1" />Abnormality</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.response_ladder.map((l) => (
            <div key={l.level} className={`rounded-lg border p-3 ${LEVEL_TONE[l.level]}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wide">{l.label}</span>
                <span className="text-xs font-medium">{l.condition}</span>
              </div>
              <p className="mt-0.5 text-[11px] opacity-90">{l.response}</p>
              <p className="text-[11px] opacity-70">e.g. {l.example}</p>
            </div>
          ))}
          <div className="grid gap-2 pt-1 sm:grid-cols-5">
            {content.respond_steps.map((s) => (
              <div key={s.step} className="rounded-lg border border-muted/60 p-2.5" data-testid={`method-respond-step-${s.step}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary">{s.step} · {s.name}</p>
                <p className="text-[10px] text-muted-foreground">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="pt-1 text-[11px] text-muted-foreground">{content.containment_rule}</p>
          <p className="text-[11px] text-muted-foreground" data-testid="method-accountability-note">
            <strong className="uppercase tracking-wide text-primary">Accountability</strong> — {content.accountability_note}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Open abnormalities</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 && <p className="text-xs text-muted-foreground">Nothing open.</p>}
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-muted/60 p-3" data-testid={`method-resp-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] uppercase border ${LEVEL_TONE[r.level]}`}>{r.level}</Badge>
                  <span className="text-sm font-medium text-foreground">{r.condition_summary}</span>
                </div>
                <div className="flex items-center gap-2">
                  {r.containment_expired && (
                    <Badge className="border border-rose-500/40 bg-rose-500/15 text-rose-700 text-[10px]" data-testid={`method-resp-expired-${r.id}`}>
                      <AlertTriangle className="h-3 w-3 mr-1" />containment expired
                    </Badge>
                  )}
                  {r.status === 'open' && (
                    <Button size="sm" variant="outline" onClick={() => setClosing(r.id)} data-testid={`method-resp-close-${r.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" />Close
                    </Button>
                  )}
                  {r.status === 'closed' && <Badge variant="secondary" className="text-[10px]">closed</Badge>}
                </div>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {r.boundary && <>Boundary: {r.boundary} · </>}
                {r.owner && <>Owner: {r.owner} · </>}
                {r.return_time && <>Back at {r.return_time}</>}
              </p>
              {r.containment && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Containment: {r.containment} · expires {(r.containment_expires_at || '').slice(0, 16).replace('T', ' ')} · removed when: {r.removal_condition}
                </p>
              )}
              {r.escalation_message && (
                <p className="mt-1 rounded border border-muted/60 bg-muted/20 p-2 text-[10px] text-muted-foreground" data-testid={`method-resp-escalation-${r.id}`}>
                  {r.escalation_message}
                </p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Avoid</CardTitle></CardHeader>
        <CardContent>
          <ul className="list-disc space-y-1 pl-5 text-[11px] text-muted-foreground">
            {content.escalation_avoid.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto" data-testid="method-respond-dialog">
          <DialogHeader>
            <DialogTitle>One-page response logic</DialogTitle>
            <DialogDescription>
              Amber and red need an owner and a return time. Red and black need a boundary and a decision-ready escalation.
              A containment needs an expiry and a removal condition.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1">
              {content.response_ladder.map((l) => (
                <button key={l.level} onClick={() => setForm((p) => ({ ...p, level: l.level }))}
                        className={`flex-1 rounded border px-2 py-1 text-[11px] font-semibold uppercase cursor-pointer ${form.level === l.level ? LEVEL_TONE[l.level] : 'border-muted text-muted-foreground'}`}
                        data-testid={`method-respond-level-${l.level}`}>{l.label}</button>
              ))}
            </div>

            {content.abnormality_logic.map((q) => (
              <div key={q.key} className="rounded-lg border border-muted/60 p-2.5" data-testid={`method-logic-${q.key}`}>
                <p className="text-xs font-medium text-foreground">{q.question}</p>
                <p className="text-[10px] text-muted-foreground">{q.evidence} → <span className="text-primary">{q.output}</span></p>
                <Input value={logic[q.key] || ''} onChange={(e) => setLogic((p) => ({ ...p, [q.key]: e.target.value }))}
                       className="mt-1.5 h-8 text-xs" data-testid={`method-logic-input-${q.key}`} />
              </div>
            ))}

            {[
              ['condition_summary', 'Condition — what is happening'],
              ['boundary', 'Boundary — what is affected, what is not'],
              ['risk', 'Risk — what may be lost'],
              ['control', 'Control — what is being done now'],
              ['decision_needed', 'Decision — what is needed, from whom'],
              ['decision_by', 'Time — latest useful decision point'],
              ['owner', 'Owner'],
              ['return_time', 'Return time'],
              ['containment', 'Containment (temporary measure, optional)'],
              ['removal_condition', 'Removed when…'],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-xs font-medium text-foreground">{label}</span>
                <Input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                       className="mt-1 h-9 text-sm" data-testid={`method-respond-${k}`} />
              </label>
            ))}
            <label className="block">
              <span className="text-xs font-medium text-foreground">Containment expires at</span>
              <Input type="datetime-local" value={form.containment_expires_at}
                     onChange={(e) => setForm((p) => ({ ...p, containment_expires_at: e.target.value }))}
                     className="mt-1 h-9 text-sm" data-testid="method-respond-expires" />
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={form.evidence_preserved}
                     onChange={(e) => setForm((p) => ({ ...p, evidence_preserved: e.target.checked }))}
                     data-testid="method-respond-evidence" />
              <span className="text-xs text-foreground">Evidence preserved (sequence, status, screens)</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={form.accountability_flag}
                     onChange={(e) => setForm((p) => ({ ...p, accountability_flag: e.target.checked }))}
                     data-testid="method-respond-accountability" />
              <span className="text-xs text-foreground">A people process applies as well (deliberate bypass, falsification, repeated non-compliance)</span>
            </label>
            {form.accountability_flag && (
              <Textarea rows={2} value={form.accountability_note}
                        onChange={(e) => setForm((p) => ({ ...p, accountability_note: e.target.value }))}
                        placeholder="The evidence behind it — recorded separately from the system condition"
                        className="text-sm" data-testid="method-respond-accountability-note" />
            )}

            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-primary">Escalation message</p>
              <p className="mt-0.5 text-[11px] text-foreground" data-testid="method-respond-preview">{preview}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="method-respond-submit">Open response</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closing} onOpenChange={(v) => !v && setClosing(null)}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)]" data-testid="method-close-dialog">
          <DialogHeader>
            <DialogTitle>Close on a verified result</DialogTitle>
            <DialogDescription>Completion is not “action done”. Record what was verified.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={closeNote} onChange={(e) => setCloseNote(e.target.value)}
                    placeholder="What was checked, when, and what it showed" className="text-sm"
                    data-testid="method-close-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)}>Cancel</Button>
            <Button onClick={close} disabled={!closeNote.trim()} data-testid="method-close-submit">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── I — Integrate ────────────────────────────────────────────────────
export const IntegrateTab = ({ content, date, deptId = '', onChanged }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ change: '', stack_level: 1, horizon: 'next_shift', owner: '' });

  const load = useCallback(async () => setRows((await getStandards('all')).rows), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await createStandard({ ...form, date, department_id: deptId, stack_level: Number(form.stack_level) });
      toast.success('Scheduled for verification');
      setForm({ change: '', stack_level: 1, horizon: 'next_shift', owner: '' });
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const verify = async (id, outcome) => {
    try { await verifyStandard(id, { outcome }); await load(); onChanged?.(); toast.success(`Marked ${outcome}`); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="method-integrate">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Integration stack</CardTitle>
          <p className="text-xs text-muted-foreground">Make the improvement survive the shift change. Date-based closure is not closure — a change closes on a verified result.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {content.integration_stack.map((s) => (
            <div key={s.level} className="flex gap-3 rounded-lg border border-muted/60 p-3">
              <span className="font-display text-lg font-bold text-primary/60">{s.level}</span>
              <div>
                <p className="text-sm font-medium text-foreground">{s.into}</p>
                <p className="text-[11px] text-muted-foreground">{s.verification}</p>
              </div>
            </div>
          ))}
          <p className="pt-1 text-[11px] text-muted-foreground" data-testid="method-no-learning-rule">
            <strong className="uppercase tracking-wide text-primary">No learning without return</strong> — {content.no_learning_without_return}
          </p>
          <div className="rounded-lg border border-muted/60 p-3">
            <p className="text-xs font-semibold text-foreground">What integration is not</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-[11px] text-muted-foreground">
              {content.integration_not.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Record a change</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input value={form.change} onChange={(e) => setForm((p) => ({ ...p, change: e.target.value }))}
                 placeholder="What changed" className="h-9 text-sm" data-testid="method-int-change" />
          <div className="grid gap-2 sm:grid-cols-3">
            <select value={form.stack_level} onChange={(e) => setForm((p) => ({ ...p, stack_level: e.target.value }))}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="method-int-level">
              {content.integration_stack.map((s) => <option key={s.level} value={s.level}>{s.level} · {s.into}</option>)}
            </select>
            <select value={form.horizon} onChange={(e) => setForm((p) => ({ ...p, horizon: e.target.value }))}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="method-int-horizon">
              {content.verification_horizons.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
            </select>
            <Input value={form.owner} onChange={(e) => setForm((p) => ({ ...p, owner: e.target.value }))}
                   placeholder="Owner" className="h-9 text-sm" data-testid="method-int-owner" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {content.verification_horizons.find((h) => h.key === form.horizon)?.question}
          </p>
          <Button size="sm" onClick={submit} disabled={!form.change.trim()} data-testid="method-int-save">Schedule verification</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Changes and their verification</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 && <p className="text-xs text-muted-foreground">Nothing recorded yet.</p>}
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-muted/60 p-3" data-testid={`method-int-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground">{r.change}</span>
                <div className="flex items-center gap-2">
                  {r.verification_due && (
                    <Badge className="border border-amber-500/40 bg-amber-500/15 text-amber-700 text-[10px]" data-testid={`method-int-due-${r.id}`}>
                      <Clock className="h-3 w-3 mr-1" />verification due
                    </Badge>
                  )}
                  {r.status === 'pending' ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => verify(r.id, 'held')} data-testid={`method-int-held-${r.id}`}>Held</Button>
                      <Button size="sm" variant="outline" onClick={() => verify(r.id, 'returned')} data-testid={`method-int-returned-${r.id}`}>Returned</Button>
                    </>
                  ) : <Badge variant="secondary" className="text-[10px]">{r.status} · {r.outcome}</Badge>}
                </div>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Stack {r.stack_level} · {(content.verification_horizons.find((h) => h.key === r.horizon) || {}).label}
                {r.owner ? ` · ${r.owner}` : ''} · check from {(r.verify_at || '').slice(0, 16).replace('T', ' ')}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export { todayIso, ShieldAlert };
